import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { Iconify } from '../../../../components';
import { MessageList } from '../../message';
import { computeSuggestedTemplates } from '../../../../utils/engagementTemplateSuggestions';

// ─────────────────────────────────────────────────────────────────────────────
// TemplateSuggestionSections
//
// Wraps the MailCompose template list with a "Suggested for this lead" /
// "All templates" two-section layout when the lead context is available AND
// at least one template is specifically targeted at this lead.
//
// Falls back to a single plain <MessageList> when:
//   • leadCtx is null (no lead selected yet — sidebar compose before lead pick)
//   • suggested.length === 0 (no templates specifically target this lead)
//
// In both fallback cases the component is a transparent wrapper — zero
// regression on any existing MailCompose path.
//
// Reusable: designed to be used by IosEmailComposerBody (Wave 3b) as well.
// Pass the same MessageList prop-set (messageListProps) that the parent
// already builds; this component just routes the `list` prop correctly.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thin section-header shown above each template bucket.
 */
export function SectionHeader({ icon, label, count }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                pt: 1,
                pb: 0.5,
            }}
        >
            {icon && (
                <Iconify
                    icon={icon}
                    width={14}
                    height={14}
                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                />
            )}
            <Typography
                variant='caption'
                sx={{ color: 'text.secondary', fontWeight: 600, lineHeight: 1 }}
            >
                {label}
            </Typography>
            {count != null && (
                <Chip
                    label={count}
                    size='small'
                    sx={{
                        height: 16,
                        fontSize: '0.65rem',
                        bgcolor: 'action.selected',
                        color: 'text.secondary',
                        '.MuiChip-label': { px: '5px' },
                    }}
                />
            )}
        </Box>
    );
}

SectionHeader.propTypes = {
    icon: PropTypes.string,
    label: PropTypes.string.isRequired,
    count: PropTypes.number,
};

SectionHeader.defaultProps = {
    icon: null,
    count: null,
};

/**
 * @param {Object}   props
 * @param {Array}    props.templates         — full flat template list
 * @param {Object|null} props.leadCtx        — from extractLeadCtx(leadDetail); null when no lead
 * @param {Object}   props.messageListProps  — all props forwarded to <MessageList>
 *                                             (does NOT include `list` — this component controls it)
 */
function TemplateSuggestionSections({ templates, leadCtx, messageListProps }) {
    const { suggested, all } = useMemo(
        () => computeSuggestedTemplates(templates, leadCtx),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [templates, leadCtx]
    );

    // No lead context or no targeted templates → plain list, no sections
    if (!leadCtx || suggested.length === 0) {
        return <MessageList {...messageListProps} list={templates} />;
    }

    return (
        <>
            {/* ── Suggested section ─────────────────────────────────────── */}
            <SectionHeader
                icon='eva:star-outline'
                label='Suggested for this lead'
                count={suggested.length}
            />
            <MessageList {...messageListProps} list={suggested} />

            {/* ── Divider ───────────────────────────────────────────────── */}
            <Divider sx={{ my: 0.5, borderStyle: 'dashed' }} />

            {/* ── All templates section ──────────────────────────────────── */}
            <SectionHeader
                label='All templates'
                count={all.length}
            />
            <MessageList {...messageListProps} list={all} />
        </>
    );
}

TemplateSuggestionSections.propTypes = {
    /** Full flat template list from Redux (templateDetailsList or subjectTemplateList). */
    templates: PropTypes.array,
    /** Lead context from extractLeadCtx(leadDetail). Null when no lead is selected. */
    leadCtx: PropTypes.shape({
        mappedStatus: PropTypes.string,
        categoryIds: PropTypes.arrayOf(PropTypes.number),
        itemIds: PropTypes.arrayOf(PropTypes.number),
    }),
    /** All props forwarded to <MessageList> — caller builds this object; `list` is injected here. */
    messageListProps: PropTypes.object.isRequired,
};

TemplateSuggestionSections.defaultProps = {
    templates: [],
    leadCtx: null,
};

export default TemplateSuggestionSections;
